import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import worldGeo from '../images/world.geo.json';
import PageHeader from '../components/PageHeader';
import { loadTrips } from '../utils/contentLoader';

const MIN_ZOOM = 1;
const MAX_ZOOM = 10;
const DELTA_ZOOM = 0.25;

// All visited country codes (sovereign a3 codes used by the GeoJSON)
const VISITED_CODES = [
  'ITA','ESP','FR1','NL1','SWE','DEU','PRT','GB1','IRL','BEL','LUX','DN1',
  'POL','HUN','AUT','SVN','CZE','HRV','GRC','TUR','NPL','VNM','MYS','RUS',
  'PHL','MAR','EGY','US1','HND','BLZ','PER',
];

// Countries where I lived (highlighted differently)
const LIVED_IN_CODES = ['ITA', 'NL1'];

// Map from sovereign a3 code → trip slug (for navigation)
const CODE_TO_SLUG = {
  ITA:'italy', ESP:'spain', FR1:'france', NL1:'netherlands', SWE:'sweden',
  DEU:'germany', PRT:'portugal', GB1:'uk', IRL:'ireland', BEL:'belgium',
  LUX:'luxembourg', DN1:'denmark', POL:'poland', HUN:'hungary', AUT:'austria',
  SVN:'slovenia', CZE:'czech-republic', HRV:'croatia', GRC:'greece',
  TUR:'turkey', NPL:'nepal', VNM:'vietnam', MYS:'malaysia', RUS:'russia',
  PHL:'philippines', MAR:'morocco', EGY:'egypt', US1:'usa', HND:'honduras',
  BLZ:'belize', PER:'peru',
};

const STATS = [
  { value: '44', label: 'Countries' },
  { value: '6',  label: 'Continents' },
  { value: '2',  label: 'Lived in' },
  { value: '0',  label: 'Lost bags' },
];

const CONTINENT_ORDER = ['Europe','Asia','Africa','America','Oceania'];

export default function Travel() {
  const navigate = useNavigate();
  const [zoom, setZoom] = useState(1.5);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);

  useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth < 768);
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener('resize', handler);
    // orientationchange fires before resize on some browsers
    window.addEventListener('orientationchange', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);

  // Show map on desktop, or on mobile when rotated to landscape
  const showMap = !isMobile || isLandscape;
  // Use a shorter map on mobile landscape to leave room for scroll
  const mapHeight = isMobile && isLandscape ? 300 : 480;

  const [sortMode, setSortMode] = useState('continent');

  const trips = useMemo(() => loadTrips(), []);

  // Group trips by continent
  const byContinent = useMemo(() => {
    const groups = {};
    trips.forEach((t) => {
      const c = t.continent || 'Other';
      if (!groups[c]) groups[c] = [];
      groups[c].push(t);
    });
    return groups;
  }, [trips]);

  // Flat alphabetical list
  const tripsAlpha = useMemo(
    () => [...trips].sort((a, b) => a.country.localeCompare(b.country)),
    [trips]
  );

  const handleCountryClick = (sov_a3) => {
    const slug = CODE_TO_SLUG[sov_a3];
    if (slug) navigate(`/travel/${slug}`);
  };

  return (
    <div style={{ padding: '48px' }}>
      <PageHeader
        eyebrow="04 — Travel"
        title="Travel"
        subtitle="44 countries, 6 continents, 0 lost luggages."
      />

      {/* Stats row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '1px',
        background: 'var(--border)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        overflow: 'hidden',
        marginBottom: '40px',
      }}>
        {STATS.map((s) => (
          <div key={s.label} style={{
            background: 'var(--surface)',
            padding: isMobile ? '14px 6px' : '20px',
            textAlign: 'center',
            overflow: 'hidden',
          }}>
            <div style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: '2.2rem',
              fontWeight: 700,
              color: 'var(--gold)',
              lineHeight: 1,
              marginBottom: '4px',
            }}>
              {s.value}
            </div>
            <div style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.58rem',
              color: 'var(--text-3)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Interactive world map (desktop + mobile landscape) */}
      {showMap && (
        <div style={{ marginBottom: '48px' }}>
          <div style={{
            position: 'relative',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            overflow: 'hidden',
            background: 'var(--surface)',
            height: `${mapHeight}px`,
          }}>
            {/* Zoom controls */}
            <div style={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}>
              {[
                { label: '+', action: () => setZoom((z) => Math.min(z + DELTA_ZOOM, MAX_ZOOM)) },
                { label: '−', action: () => setZoom((z) => Math.max(z - DELTA_ZOOM, MIN_ZOOM)) },
              ].map(({ label, action }) => (
                <button
                  key={label}
                  onClick={action}
                  style={{
                    width: '28px',
                    height: '28px',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text-2)',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Legend */}
            <div style={{
              position: 'absolute',
              bottom: '12px',
              right: '12px',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              padding: '10px 14px',
            }}>
              {[
                { color: '#c4a24a', label: 'Visited' },
                { color: '#7cb8a0', label: 'Lived in' },
              ].map(({ color, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: color, flexShrink: 0 }} />
                  <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.58rem', color: 'var(--text-3)', letterSpacing: '0.05em' }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>

            <p style={{
              position: 'absolute',
              bottom: '12px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.58rem',
              color: 'var(--text-3)',
              margin: 0,
              whiteSpace: 'nowrap',
            }}>
              Click a highlighted country to read the story
            </p>

            <ComposableMap projection="geoNaturalEarth1" style={{ width: '100%', height: '100%' }}>
              <ZoomableGroup zoom={zoom} center={[0, 10]} minZoom={MIN_ZOOM} maxZoom={MAX_ZOOM}>
                <Geographies geography={worldGeo}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const code = geo.properties.sov_a3;
                      const isVisited = VISITED_CODES.includes(code);
                      const isLivedIn = LIVED_IN_CODES.includes(code);
                      const isClickable = isVisited && CODE_TO_SLUG[code];

                      let fill = '#2a2835';
                      if (isLivedIn) fill = '#7cb8a0';
                      else if (isVisited) fill = '#c4a24a';

                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={fill}
                          stroke="#1a1820"
                          strokeWidth={0.4}
                          style={{
                            default: { outline: 'none' },
                            hover: {
                              outline: 'none',
                              fill: isClickable ? (isLivedIn ? '#9dd4bc' : '#d4b86a') : fill,
                              cursor: isClickable ? 'pointer' : 'default',
                            },
                            pressed: { outline: 'none' },
                          }}
                          onClick={() => isClickable && handleCountryClick(code)}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
          </div>
          <p style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontSize: '0.85rem',
            color: 'var(--text-3)',
            marginTop: '10px',
            textAlign: 'center',
          }}>
            Some colored spots are overseas territories left over from European imperialistic endeavors.
            Let's see if you know them all! <em>(I did not...)</em>
          </p>
        </div>
      )}

      {/* Controls row: rotate hint (mobile portrait only) + sort */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px', flexWrap: 'wrap' }}>
        {isMobile && !isLandscape && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '5px 12px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
            </svg>
            <span style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.65rem',
              color: 'var(--text-3)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}>
              Rotate for map
            </span>
          </div>
        )}

        {/* Divider — only when both are visible */}
        {isMobile && !isLandscape && (
          <div style={{ width: '1px', height: '18px', background: 'var(--border)' }} />
        )}

        <span style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.6rem',
          color: 'var(--text-3)',
          letterSpacing: '0.06em',
        }}>
          Sort:
        </span>
        {[
          { key: 'continent', label: 'By continent' },
          { key: 'alpha',     label: 'A–Z' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setSortMode(key)}
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.65rem',
              letterSpacing: '0.06em',
              padding: '5px 12px',
              borderRadius: '4px',
              border: '1px solid',
              cursor: 'pointer',
              background: sortMode === key ? 'var(--gold)' : 'transparent',
              borderColor: sortMode === key ? 'var(--gold)' : 'var(--border)',
              color: sortMode === key ? '#0d0c10' : 'var(--text-3)',
              transition: 'all 0.15s ease',
              textTransform: 'uppercase',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Trip cards */}
      {sortMode === 'alpha' ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: '10px',
          marginBottom: '48px',
        }}>
          {tripsAlpha.map((trip) => (
            <TripCard key={trip.slug} trip={trip} />
          ))}
        </div>
      ) : (
        CONTINENT_ORDER.filter((c) => byContinent[c]).map((continent) => (
          <div key={continent} style={{ marginBottom: '48px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '20px',
            }}>
              <div className="eyebrow">{continent}</div>
              <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: '10px',
            }}>
              {byContinent[continent].map((trip) => (
                <TripCard key={trip.slug} trip={trip} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function TripCard({ trip }) {
  const hasFullStory = trip.contentHtml && !trip.contentHtml.includes('Story coming soon');

  return (
    <Link
      to={`/travel/${trip.slug}`}
      style={{ textDecoration: 'none' }}
    >
      <div
        className="card-hover"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          overflow: 'hidden',
          borderLeft: `3px solid ${hasFullStory ? 'var(--gold)' : 'var(--border-2)'}`,
        }}
      >
        {/* Thumbnail */}
        {trip.thumbnail && (
          <div style={{ height: '100px', background: 'var(--surface-2)', overflow: 'hidden' }}>
            <img
              src={trip.thumbnail}
              alt={trip.country}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
            />
          </div>
        )}

        <div style={{ padding: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
            <h3 style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: '1.1rem',
              fontWeight: 600,
              color: 'var(--text)',
              margin: 0,
              letterSpacing: '-0.01em',
            }}>
              {trip.country}
            </h3>
            <span style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.58rem',
              color: 'var(--text-3)',
              flexShrink: 0,
              marginLeft: '8px',
            }}>
              {trip.year === 'ongoing' ? '∞' : trip.year}
            </span>
          </div>

          {trip.description && (
            <p style={{
              fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
              fontSize: '0.72rem',
              color: 'var(--text-3)',
              margin: 0,
              lineHeight: 1.45,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {trip.description}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}
